import { Check } from "lucide-react";

/**
 * Premium pricing card matching Wertbild editorial design.
 *
 * Props:
 *   name        – package name (e.g. "Plus")
 *   price       – integer euro amount (e.g. 399)
 *   addonPrice  – optional integer for "Als Add-on zum Fotopaket: +XXX €"
 *   tagline     – short one-liner under the price
 *   description – longer descriptive paragraph
 *   features    – array of strings (bullet points)
 *   deliveryTime – optional delivery-time string ("24–48 Stunden")
 *   featured    – if true, highlights card with border + slight scale + badge
 *   badge       – optional badge text
 */
export default function PricingCard({
  name,
  price,
  addonPrice,
  tagline,
  description,
  features = [],
  deliveryTime,
  featured = false,
  badge,
}) {
  return (
    <div
      data-testid={`pricing-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className={`relative flex flex-col h-full bg-[#F8F5EF] p-8 md:p-10 transition-all duration-500 reveal ${
        featured
          ? "border-2 border-[#FFA500] md:-my-4 shadow-[0_28px_60px_-24px_rgba(255,165,0,0.35)] bg-white"
          : "border border-[#EAE6DE] hover:bg-white hover:border-[#1A1A1A]/25"
      }`}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFA500] text-[#1A1A1A] text-[10px] uppercase tracking-[0.24em] font-medium px-4 py-1.5">
          {badge}
        </div>
      )}

      <div className="overline text-[#5A5A5A] mb-4">{name}</div>

      <div className="flex items-baseline gap-2">
        <span className="ticker-num text-6xl md:text-7xl text-[#1A1A1A] leading-none">{price}</span>
        <span className="ticker-num text-3xl text-[#1A1A1A]">€</span>
      </div>
      <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#5A5A5A]">zzgl. MwSt.</div>

      {addonPrice != null && (
        <div className="mt-4 text-[13px] text-[#5A5A5A] font-light">
          Als Add-on zum Fotopaket: <span className="text-[#FFA500] font-medium">+{addonPrice} €</span>
        </div>
      )}

      {tagline && (
        <p className="mt-6 font-display italic text-[18px] md:text-[20px] leading-snug text-[#1A1A1A]">
          {tagline}
        </p>
      )}

      {description && (
        <p className="mt-4 text-[14px] leading-relaxed text-[#5A5A5A] font-light">{description}</p>
      )}

      <div className="my-8 h-px bg-[#EAE6DE]"></div>

      <ul className="space-y-4 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-[14px] leading-relaxed text-[#1A1A1A]">
            <span className="mt-1 w-5 h-5 flex items-center justify-center bg-[#FFA500] shrink-0">
              <Check size={11} className="text-[#1A1A1A]" strokeWidth={3} />
            </span>
            <span className="font-light">{f}</span>
          </li>
        ))}
      </ul>

      {deliveryTime && (
        <div className="mt-8 text-[12px] uppercase tracking-[0.2em] text-[#5A5A5A]">
          Lieferzeit · <span className="text-[#1A1A1A]">{deliveryTime}</span>
        </div>
      )}
    </div>
  );
}
